import type { CSSProperties } from 'react';

export interface AvatarPerson {
  id: string | number;
  name: string;
  initials?: string;
  title?: string;
}

const PALETTE = [
  '#C55A3C', '#5B6F8F', '#4A7C59', '#A88E54', '#7A4A6C',
  '#3F6E78', '#9A4A2E', '#5C5C7E', '#6B8E5A',
];

function initialsFor(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
}

function colorFor(id: string | number): string {
  const key = String(id);
  const last = key.charCodeAt(key.length - 1) || 0;
  return PALETTE[last % PALETTE.length];
}

export interface AvatarProps {
  person: AvatarPerson;
  size?: number;
  ring?: boolean;
  style?: CSSProperties;
}

export function Avatar({ person, size = 28, ring = false, style }: AvatarProps) {
  const initials = person.initials ?? initialsFor(person.name);
  return (
    <div
      title={person.title ? `${person.name} · ${person.title}` : person.name}
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        background: colorFor(person.id),
        color: '#fff',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: size * 0.38,
        fontWeight: 600,
        letterSpacing: '0.02em',
        border: ring ? '2px solid var(--apm-card)' : 'none',
        boxShadow: ring ? '0 0 0 1px var(--apm-line-2)' : 'none',
        flex: '0 0 auto',
        ...style,
      }}
    >
      {initials}
    </div>
  );
}

export interface AvatarStackProps {
  people: AvatarPerson[];
  max?: number;
  size?: number;
}

export function AvatarStack({ people, max = 4, size = 24 }: AvatarStackProps) {
  const shown = people.slice(0, max);
  const more = people.length - shown.length;
  return (
    <div style={{ display: 'inline-flex' }}>
      {shown.map((p, i) => (
        <div key={p.id} style={{ marginLeft: i === 0 ? 0 : -size * 0.28 }}>
          <Avatar person={p} size={size} ring />
        </div>
      ))}
      {more > 0 && (
        <div
          style={{
            width: size,
            height: size,
            borderRadius: '50%',
            background: 'var(--apm-bg-sunken)',
            color: 'var(--apm-muted)',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: size * 0.36,
            fontWeight: 600,
            marginLeft: -size * 0.28,
            border: '2px solid var(--apm-card)',
          }}
        >
          +{more}
        </div>
      )}
    </div>
  );
}
